import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleCTAClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('https://calendly.com/beratung-ciafardini-consulting/30min', '_blank');
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? 'py-1 md:py-2 bg-white shadow-md' : 'py-2 md:py-4 bg-white'}`}>
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src="/images/benefitwerk logo.png" 
              alt="BenefitWerk Logo" 
              className="h-14 md:h-16 w-auto object-contain transition-all duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <NavLinks />
          </div>

          {/* Call to Action Button */}
          <div className="hidden md:block">
            <a 
              href="#"
              onClick={handleCTAClick}
              className="btn btn-primary btn-lg"
            >
              Kostenfreie Potentialanalyse
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-primary-500 focus:outline-none"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 mt-2">
          <div className="container mx-auto px-6 py-4">
            <div className="flex flex-col space-y-4">
              <MobileNavLinks closeMenu={() => setIsOpen(false)} />
              <a 
                href="#"
                onClick={(e) => {
                  setIsOpen(false);
                  handleCTAClick(e);
                }}
                className="btn btn-primary text-center mt-4 py-2"
              >
                Kostenfreie Potentialanalyse
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Navigation links component for desktop
const NavLinks = () => {
  const handleCTAClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('https://calendly.com/beratung-ciafardini-consulting/30min', '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <DropdownMenu 
        title="Leistungen" 
        items={[
          { label: 'Unternehmensvorteile', href: '#benefits-highlights' },
          { label: 'Herausforderungen', href: '#pain-points' },
          { label: 'Gesundheit', href: '#health-is' }
        ]} 
      />
      <a href="#employee-employer" className="px-4 py-2 text-gray-700 hover:text-primary-500 font-medium">Vorteile</a>
      <a href="#how-it-works" className="px-4 py-2 text-gray-700 hover:text-primary-500 font-medium">Unser Vorgehen</a>
      <a href="#faq" className="px-4 py-2 text-gray-700 hover:text-primary-500 font-medium">FAQ</a>
    </>
  );
};

// Navigation links component for mobile
const MobileNavLinks = ({ closeMenu }: { closeMenu: () => void }) => {
  const handleCTAClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open('https://calendly.com/beratung-ciafardini-consulting/30min', '_blank');
    closeMenu();
  };

  return (
    <>
      <MobileDropdownMenu 
        title="Leistungen" 
        items={[
          { label: 'Unternehmensvorteile', href: '#benefits-highlights' },
          { label: 'Herausforderungen', href: '#pain-points' },
          { label: 'Gesundheit', href: '#health-is' }
        ]}
        closeMenu={closeMenu}
      />
      <a 
        href="#employee-employer" 
        className="block px-4 py-2 text-gray-700 hover:text-primary-500 border-b border-gray-100"
        onClick={closeMenu}
      >
        Vorteile
      </a>
      <a 
        href="#how-it-works" 
        className="block px-4 py-2 text-gray-700 hover:text-primary-500 border-b border-gray-100"
        onClick={closeMenu}
      >
        Unser Vorgehen
      </a>
      <a 
        href="#faq" 
        className="block px-4 py-2 text-gray-700 hover:text-primary-500"
        onClick={closeMenu}
      >
        FAQ
      </a>
    </>
  );
};

// Dropdown menu component for desktop
const DropdownMenu = ({ title, items }: { title: string, items: { label: string, href: string }[] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    // Add a slight delay before closing the dropdown
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div 
      className="relative" 
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button 
        className="flex items-center px-4 py-2 text-gray-700 hover:text-primary-500 font-medium"
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={0}
      >
        {title}
        <ChevronDown size={16} className={`ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div 
          className="absolute left-0 mt-1 w-60 bg-white rounded-md shadow-lg z-10 transition-all duration-200"
        >
          <div className="py-1">
            {items.map((item, index) => (
              <a 
                key={index}
                href={item.href}
                className="block px-4 py-2 text-gray-700 hover:bg-secondary-200 hover:text-primary-500"
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Dropdown menu component for mobile
const MobileDropdownMenu = ({ 
  title, 
  items, 
  closeMenu 
}: { 
  title: string, 
  items: { label: string, href: string }[], 
  closeMenu: () => void 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-100">
      <button 
        className="flex items-center justify-between w-full px-4 py-2 text-gray-700 hover:text-primary-500"
        onClick={() => setIsOpen(!isOpen)}
      >
        {title}
        <ChevronDown size={16} className={`ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="pl-4 py-2 bg-gray-50">
          {items.map((item, index) => (
            <a 
              key={index}
              href={item.href}
              className="block px-4 py-2 text-gray-700 hover:text-primary-500"
              onClick={() => {
                setIsOpen(false);
                closeMenu();
              }}
            >
              {item.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default Navbar;